import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  AzureMap,
  AzureMapsProvider,
  AzureMapDataSourceProvider,
  AzureMapFeature,
  AzureMapLayerProvider,
  AzureMapPopup, AzureMapsContext
} from 'react-azure-maps';
import { AuthenticationType, data } from 'azure-maps-control';
import i18n from 'localization/i18n';
import { I18nextProvider } from 'react-i18next';
import { useQueryClient, QueryClientProvider } from '@tanstack/react-query';
import NodeContext from 'services/Context/NodeContext';

const option = {
  showLogo: false,
  showFeedbackLink: false,
  authOptions: {
    authType: AuthenticationType.subscriptionKey,
    subscriptionKey: '2i71cKZ5sdR1MUUagIYhOUXztPuDJtbJZo8YBicMmBo'
  }
};

const renderPoint = ({ id, point }) => {
  const key = uuidv4();
  return (<AzureMapFeature
    key={key}
    id={key}
    type="Point"
    coordinate={new data.Position(point.long, point.lat)}
    properties={{
      icon: 'pin-round-blue',
      id
    }}
  />);
};

const layerOptions = {
  textOptions: {
    offset: [0, 1.2]
  }
};

export const MapStyles = Object.freeze([
  'road',
  'satellite',
  'satellite_road_labels',
  'grayscale_dark',
  'grayscale_light',
  'night',
  'road_shaded_relief',
  'high_contrast_dark',
  'high_contrast_light'
]);

export const MapControls = Object.freeze({
  STYLE: 'StyleControl',
  ZOOM: 'ZoomControl'
});

const AllControls = [
  {
    controlName: MapControls.STYLE,
    controlOptions: { mapStyles: 'all' },
    options: { position: 'top-right' }
  },
  {
    controlName: MapControls.ZOOM,
    options: { position: 'top-right' }
  }
];

const PopupContentWrapper = ({ nodeData, queryClient, children }) => {
  // Need to provide some context again. Something with the way the map is rendered with azure
  // TODO: Find better solution to this if we want to render more complex views in popups
  return (
    <NodeContext.Provider value={nodeData}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          { children }
        </I18nextProvider>
      </QueryClientProvider>
    </NodeContext.Provider>
  );
};

const MapContent = ({ points, controls = [MapControls.STYLE], mapStyles = ['road', 'satellite'] }) => {
  const memoizedMarkerRender = useMemo(() =>
    _.map(_.filter(points, point => point.lat != null && point.long != null),
      (point, id) => renderPoint({ point, id })),
  [points]);
  const [popups, setPopups] = useState([]);
  const { mapRef, isMapReady } = useContext(AzureMapsContext);
  const queryClient = useQueryClient();
  const nodeData = useContext(NodeContext);

  const usedControls = useMemo(() => {
    const enabledControls = _.filter(AllControls, control => _.includes(controls, control.controlName));
    return _.map(enabledControls, control => {
      if (control.controlName === MapControls.STYLE) {
        return {
          ...control,
          controlOptions: {
            ...control.controlOptions,
            mapStyles: _.isEmpty(mapStyles) ? MapStyles : mapStyles
          }
        };
      }
      return control;
    }
    );
  }, [controls, mapStyles]);

  useEffect(() => {
    if (mapRef && isMapReady) {
      mapRef.resize();
    }
  }, [isMapReady, mapRef]);

  useEffect(() => {
    if (mapRef && isMapReady && !_.isEmpty(points)) {
      const bounds = data.BoundingBox.fromPositions(_.map(points, point => new data.Position(point.long, point.lat)));
      mapRef.setCamera({ bounds, maxZoom: 15, padding: 50 });
    }
  }, [isMapReady, mapRef, points]);

  useEffect(() => {
    // Clear popups when points change
    setPopups([]);
  }, [points]);

  const itemClicked = useCallback((e) => {
    if (e.shapes && e.shapes.length > 0) {
      const prop = e.shapes[0];
      setPopups(oldPopups => {
        if (oldPopups?.length > 0 && oldPopups[0].id === prop.data.properties?.id) {
          return [];
        }
        if (prop.data.geometry == null) {
          return [];
        }
        return [{
          isVisible: true,
          options: {
            position: new data.Position(prop.data.geometry.coordinates[0], prop.data.geometry.coordinates[1]),
            pixelOffset: [0, 0]
          },
          id: prop.data.properties?.id
        }];
      });
    }
  }, []);

  const popupComponents = useMemo(() => {
    return _.map(popups, popup => {
      return (<AzureMapPopup
        key={uuidv4()}
        isVisible={popup.isVisible}
        options={popup.options}
        popupContent={<PopupContentWrapper nodeData={nodeData} queryClient={queryClient}>{ points[popup.id]?.content }</PopupContentWrapper>}
      />);
    });
  }, [nodeData, points, popups, queryClient]);

  return (
    <AzureMap options={option}
      controls={usedControls}
    >
      <AzureMapDataSourceProvider
        id={'AzureMapDataSourceProvider'}
      >
        <AzureMapLayerProvider
          id={'AzureMapLayerProvider'}
          options={layerOptions}
          events={{
            click: itemClicked
          }}
          type={'SymbolLayer'}
        />
        { memoizedMarkerRender }
      </AzureMapDataSourceProvider>
      { popupComponents }
    </AzureMap>
  );
};

const Map = (props) => {
  return <AzureMapsProvider><MapContent {...props} /></AzureMapsProvider>;
};
export default Map;
